/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import { getQueryClient } from '@opengeoweb/api';
import {
  getAxiosErrorMessage,
  isAuthError,
  isAxiosError,
} from '@opengeoweb/shared';
import { snackbarActions } from '@opengeoweb/snackbar';
import { routerActions } from '@opengeoweb/store';
import { workspaceListSelectors } from '.';
import { workspaceListActions } from './reducer';
import type {
  OnSuccesWorkspacePresetActionPayload,
  SubmitFormWorkspaceActionDialogPayload,
} from './types';
import { WorkspaceListErrorCategory, WorkspaceListErrorType } from './types';
import { getEmptyMapWorkspace, getSnackbarMessage } from './utils';
import { workspaceQueryOptions } from '../../api/workspace-api/queryOptions';
import { getWorkspaceRouteUrl } from '../../utils/routes';
import type { AppStore } from '../types';
import { workspaceActions } from '../workspace/reducer';
import {
  hasWorkspaceChanges,
  getWorkspaceToSendToBackend,
} from '../workspace/selectors';
import { WorkspacePresetAction, WorkspaceErrorType } from '../workspace/types';
import type {
  WorkspacePresetForBE,
  WorkspaceViewsFromBE,
} from '../workspace/types';

export const emptyWorkspaceBEData: Omit<WorkspacePresetForBE, 'title'> = {
  ...getEmptyMapWorkspace(),
  views: [
    {
      mosaicNodeId: '1_screen',
      viewPresetId: 'emptyMap',
    },
  ],
};

export const workspaceListListener = createListenerMiddleware<AppStore>();

workspaceListListener.startListening({
  actionCreator: workspaceListActions.onSuccessWorkspacePresetAction,
  effect: async (
    { payload }: PayloadAction<OnSuccesWorkspacePresetActionPayload>,
    listenerApi,
  ) => {
    listenerApi.cancelActiveListeners();

    const currentWorkspaceId = listenerApi.getOriginalState().workspace?.id;
    const credentials = listenerApi.getState().common?.auth?.credentials;

    const { action, workspaceId, title } = payload;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const is_shared = 'is_shared' in payload ? payload.is_shared : undefined;

    await getQueryClient().invalidateQueries(
      workspaceQueryOptions.getWorkspacePreset(workspaceId, credentials),
    );
    await getQueryClient().invalidateQueries({
      queryKey: workspaceQueryOptions.keys.lists(),
    });

    listenerApi.dispatch(
      snackbarActions.openSnackbar(
        getSnackbarMessage(action, title, is_shared),
      ),
    );
    // if active workspace has been deleted, empty out url
    if (
      (action === WorkspacePresetAction.DELETE ||
        action === WorkspacePresetAction.DELETE_SYSTEM_PRESET) &&
      currentWorkspaceId &&
      workspaceId === currentWorkspaceId
    ) {
      listenerApi.dispatch(
        routerActions.navigateToUrl({ url: getWorkspaceRouteUrl() }),
      );
    }

    // For Save as, update url to include the new id
    if (action === WorkspacePresetAction.SAVE_AS) {
      listenerApi.dispatch(
        routerActions.navigateToUrl({
          url: getWorkspaceRouteUrl(workspaceId),
        }),
      );
    }
  },
});

workspaceListListener.startListening({
  actionCreator: workspaceListActions.submitFormWorkspaceActionDialogOptions,
  effect: async (
    { payload }: PayloadAction<SubmitFormWorkspaceActionDialogPayload>,
    listenerApi,
  ) => {
    listenerApi.cancelActiveListeners();
    const workspaceState = listenerApi.getState();
    const credentials = listenerApi.getState().common?.auth?.credentials;
    const {
      presetId,
      presetAction,
      data,
      emptyMapPresetTitle,
      emptyWorkspacePresetTitle,
      defaultPreset,
    } = payload;
    const scope = data.scope || 'user';

    try {
      // duplicate
      if (presetAction === WorkspacePresetAction.DUPLICATE) {
        // Should no longer be possible
      }
      // delete
      if (
        presetAction === WorkspacePresetAction.DELETE ||
        presetAction === WorkspacePresetAction.DELETE_SYSTEM_PRESET
      ) {
        // Should no longer be possible
      }

      // edit
      if (presetAction === WorkspacePresetAction.EDIT) {
        // Retrieve original workspace data
        const originalData = await getQueryClient().fetchQuery(
          workspaceQueryOptions.getWorkspacePreset(presetId, credentials),
        );

        const workspaceDataNewTitle = {
          ...originalData,
          title: data.title,
          abstract: data.abstract,
          description: data.description,
          labels: data.labels,
          updated: data.updated,
          updatedBy: data.updatedBy,
          username: data.username,
        };
        await getQueryClient().fetchQuery(
          workspaceQueryOptions.saveWorkspacePreset(
            presetId,
            workspaceDataNewTitle,
            credentials,
          ),
        );
        await getQueryClient().invalidateQueries(
          workspaceQueryOptions.getWorkspacePreset(presetId, credentials),
        );

        listenerApi.dispatch(
          workspaceListActions.onSuccessWorkspacePresetAction({
            workspaceId: presetId,
            action: presetAction,
            title: data.title,
            abstract: data.abstract,
            description: data.description,
            labels: data.labels,
            updated: data.updated,
            updatedBy: data.updatedBy,
            username: data.username,
            scope,
          }),
        );
      }

      if (presetAction === WorkspacePresetAction.EDIT_TITLE) {
        // 1) Get original workspace
        const originalData = await getQueryClient().fetchQuery(
          workspaceQueryOptions.getWorkspacePreset(presetId, credentials),
        );

        // 2) Merge the single (or few) edited view titles from `data.views`
        const mergedViews = (originalData.views ?? []).map(
          (view: WorkspaceViewsFromBE) => {
            const updated = (data.views ?? []).find(
              (vw: WorkspaceViewsFromBE) =>
                vw.mosaicNodeId === view.mosaicNodeId,
            );
            return updated
              ? { ...view, title: updated.title ?? view.title }
              : view;
          },
        );

        // 3) Save back WITHOUT changing workspace title/abstract
        const payloadToSave = {
          ...originalData,
          views: mergedViews,
        };
        await getQueryClient().fetchQuery(
          workspaceQueryOptions.saveWorkspacePreset(
            presetId,
            payloadToSave,
            credentials,
          ),
        );
        await getQueryClient().invalidateQueries(
          workspaceQueryOptions.getWorkspacePreset(presetId, credentials),
        );

        // 4) Notify success so reducer updates local state
        listenerApi.dispatch(
          workspaceListActions.onSuccessWorkspacePresetAction({
            workspaceId: presetId,
            action: WorkspacePresetAction.EDIT_TITLE,
            // keep original workspace title/abstract untouched
            title: originalData.title,
            abstract: originalData.abstract,
            username: originalData.username,
            scope,
            // you can send only the edited ones, or the merged full list.
            // Reducer uses mosaicNodeId+title from `views` to patch state.
            views: data.views ?? [],
          }),
        );
      }

      // save (as) user/system presets
      const isSaveWorkspaceAction =
        presetAction === WorkspacePresetAction.SAVE_SYSTEM_PRESET ||
        presetAction === WorkspacePresetAction.SAVE;

      const isSaveAsWorkspaceAction =
        presetAction === WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS ||
        presetAction === WorkspacePresetAction.SAVE_AS;

      if (isSaveWorkspaceAction || isSaveAsWorkspaceAction) {
        const hasWorkspaceAlreadyBeenSaved =
          workspaceListSelectors.getWorkspaceDialogIsSaved(workspaceState);

        let workspaceId = presetId;

        const workspaceData = getWorkspaceToSendToBackend(
          listenerApi.getState(),
        );

        let newWorkspace: WorkspacePresetForBE = {
          ...workspaceData,
          id: workspaceId,
          title: data.title,
          abstract: data.abstract,
          description: data.description,
          labels: data.labels,
          scope,
          views: workspaceData.views,
          username: credentials?.username || '',
        };

        if (!hasWorkspaceAlreadyBeenSaved && isSaveAsWorkspaceAction) {
          const credentials = listenerApi.getState().common?.auth?.credentials;
          workspaceId = await getQueryClient().fetchQuery(
            workspaceQueryOptions.saveWorkspacePresetAs(
              newWorkspace,
              credentials,
            ),
          );
          newWorkspace = { ...newWorkspace, id: workspaceId };
        }

        listenerApi.dispatch(
          workspaceListActions.onSuccessWorkspaceSaveDialog({
            presetId: workspaceId,
          }),
        );

        if (!isSaveAsWorkspaceAction) {
          const hasChanges = hasWorkspaceChanges(listenerApi.getState());
          if (hasChanges) {
            await getQueryClient().fetchQuery(
              workspaceQueryOptions.saveWorkspacePreset(
                workspaceId!,
                newWorkspace,
                credentials,
              ),
            );
            await getQueryClient().invalidateQueries(
              workspaceQueryOptions.getWorkspacePreset(presetId, credentials),
            );
          }
        }

        listenerApi.dispatch(
          workspaceListActions.onSuccessWorkspacePresetAction({
            action: presetAction,
            workspaceId,
            title: newWorkspace.title,
            abstract: newWorkspace.abstract,
            description: newWorkspace.description,
            labels: newWorkspace.labels,
            username: newWorkspace.username,
            scope,
          }),
        );

        listenerApi.dispatch(
          routerActions.navigateToUrl({
            url: getWorkspaceRouteUrl(workspaceId),
          }),
        );
      }

      // Reset
      if (presetAction === WorkspacePresetAction.RESET) {
        // In case we're resetting the default preset that opens the empty workspace
        if (!presetId) {
          listenerApi.dispatch(
            workspaceActions.changeToEmptyMapWorkspace({
              newMapPresetText: emptyMapPresetTitle,
              newWorkspaceText: emptyWorkspacePresetTitle,
            }),
          );
        } else if (defaultPreset) {
          listenerApi.dispatch(
            workspaceActions.changePreset({ workspacePreset: defaultPreset }),
          );
        } else {
          listenerApi.dispatch(
            workspaceActions.fetchWorkspace({ workspaceId: presetId }),
          );
        }
        // workspace success handling
        listenerApi.dispatch(
          workspaceListActions.onSuccessWorkspacePresetAction({
            action: WorkspacePresetAction.RESET,
            workspaceId: presetId!,
            title: data.title,
            abstract: data.abstract,
            description: data.description,
            labels: data.labels,
            scope,
          }),
        );
      }
    } catch (error) {
      const message = isAxiosError(error as Error)
        ? getAxiosErrorMessage(error as AxiosError)
        : (error as Error).message;

      if (
        presetAction === WorkspacePresetAction.SAVE ||
        presetAction === WorkspacePresetAction.SAVE_SYSTEM_PRESET
      ) {
        listenerApi.dispatch(
          workspaceActions.errorWorkspace({
            workspaceId: presetId || listenerApi.getState().workspace?.id || '',
            error: {
              message,
              type: WorkspaceErrorType.SAVE,
            },
          }),
        );
      }

      const category = isAuthError(error as AxiosError | Error)
        ? WorkspaceListErrorCategory.AUTH
        : WorkspaceListErrorCategory.GENERIC;
      listenerApi.dispatch(
        workspaceListActions.onErrorWorkspacePresetAction({
          error: {
            type: WorkspaceListErrorType.VERBATIM_ERROR,
            category,
            message,
          },
        }),
      );
    }
  },
});
